import type { Destination, InstalledDestination } from "./destinations";

/**
 * What this device has installed, and when it was last confirmed.
 *
 * The policy lives here as plain functions over a list; persistence is the
 * caller's (`storage.ts` keeps the list in the `meta` row). That split is not
 * ceremony — the rules below are the part worth testing, and the test
 * environment has no IndexedDB.
 *
 * **An install is device-local and there is no account to restore it from.**
 * Someone who clears their site data has lost their destinations and needs the
 * codes again, exactly as they have lost their projects. Say so where an
 * install is removed, rather than letting someone discover it.
 */

/**
 * How long an install is trusted before the catalogue is asked again.
 *
 * A day. Short enough that a withdrawn code stops working within one, long
 * enough that an app opened ten times in an afternoon asks once. The check is
 * never blocking: a destination stays usable while it is being re-checked, and
 * stays usable if the network is down. Offline must not uninstall anything.
 */
export const RECHECK_AFTER_MS = 24 * 60 * 60 * 1000;

/** Add or refresh an install, keeping the list keyed by destination id. */
export function withInstall(
  installed: readonly InstalledDestination[],
  destination: Destination,
  code: string,
  now: number,
  origin: InstalledDestination["origin"] = "code",
): InstalledDestination[] {
  const existing = installed.find(
    (entry) => entry.destination.id === destination.id,
  );
  const rest = installed.filter(
    (entry) => entry.destination.id !== destination.id,
  );
  return [
    ...rest,
    {
      destination,
      code,
      origin,
      // Re-installing something keeps the ORIGINAL date: it is when this
      // device gained the destination, and it decides which provider wins a
      // service. A re-check must not silently reorder that.
      installedAt: existing?.installedAt ?? now,
      checkedAt: now,
    },
  ].sort((a, b) => a.installedAt - b.installedAt);
}

export function withoutInstall(
  installed: readonly InstalledDestination[],
  destinationId: string,
): InstalledDestination[] {
  return installed.filter((entry) => entry.destination.id !== destinationId);
}

/**
 * Which installs are old enough to re-check.
 *
 * Never a host install. It has no code, so there is nothing to present and
 * nothing to withdraw — and asking the catalogue about an empty code comes
 * back "no such destination", which would uninstall the deploy the user is
 * standing on.
 */
export function dueForRecheck(
  installed: readonly InstalledDestination[],
  now: number,
): InstalledDestination[] {
  return installed.filter(
    (entry) =>
      entry.origin === "code" && now - entry.checkedAt >= RECHECK_AFTER_MS,
  );
}

/** What a re-check concluded about one install. */
export type RecheckVerdict =
  /** Still in the catalogue; its config may have changed. */
  | { kind: "kept"; destination: Destination }
  /** The catalogue says no. Remove it. */
  | { kind: "revoked" }
  /** We could not ask. Change nothing. */
  | { kind: "unknown" };

/**
 * Fold a verdict back into the list.
 *
 * `unknown` is the important case and it does nothing on purpose. An install
 * must not be removed because a user opened the app on a train: the only thing
 * that uninstalls a destination is the catalogue saying so, out loud.
 */
export function applyRecheck(
  installed: readonly InstalledDestination[],
  destinationId: string,
  verdict: RecheckVerdict,
  now: number,
): InstalledDestination[] {
  if (verdict.kind === "unknown") return [...installed];
  if (verdict.kind === "revoked") {
    return withoutInstall(installed, destinationId);
  }
  // The code is whatever this device already holds: a re-check presents the
  // stored code, so a kept verdict cannot have arrived under a different one.
  const existing = installed.find(
    (entry) => entry.destination.id === destinationId,
  );
  return withInstall(
    installed,
    verdict.destination,
    existing?.code ?? "",
    now,
    existing?.origin ?? "code",
  );
}

/* ── talking to the catalogue ──────────────────────────────────────────── */

/** Why a redemption did not produce a destination. */
export type InstallFailure =
  /** The catalogue has no such code — or had one and no longer does. */
  | "unknown-code"
  /** We never got an answer. Says nothing about the code. */
  | "unreachable"
  /** The deployment has no catalogue. Nothing to do with the code typed. */
  | "not-configured"
  /** We got an answer and could not use it. */
  | "error";

/** What came back from a redemption attempt. */
export type InstallResult =
  | { ok: true; destination: Destination }
  | { ok: false; failure: InstallFailure; reason: string };

/**
 * Redeem a code against this app's own worker.
 *
 * Not against a destination — the destination does not know its own code, and
 * a platform like YouTube certainly does not. `/api/install` is served by
 * o-cam.app itself and is the one API call that works with no session.
 */
export async function installByCode(
  code: string,
  fetchImpl: typeof fetch = fetch,
): Promise<InstallResult> {
  const trimmed = code.trim();
  if (!trimmed) {
    return { ok: false, failure: "unknown-code", reason: "Enter a code." };
  }
  let res: Response;
  try {
    res = await fetchImpl("/api/install", {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({ code: trimmed }),
    });
  } catch {
    // Distinguished from a bad code deliberately: "that code is wrong" sends
    // someone hunting for a typo in a code that is perfectly good.
    return {
      ok: false,
      failure: "unreachable",
      reason: "Could not reach the server. Try again.",
    };
  }
  if (res.status === 404) {
    return {
      ok: false,
      failure: "unknown-code",
      reason: "That code does not match a destination.",
    };
  }
  if (res.status === 503) {
    /*
     * The deployment has no catalogue bound. Pass the server's own sentence
     * through rather than flattening it to "something went wrong" — the whole
     * point of distinguishing this case is that the user's code was fine and
     * they should not go looking at it.
     */
    let message = "Destinations are not set up on this deployment yet.";
    try {
      const body = (await res.json()) as { error?: { message?: string } };
      if (body?.error?.message) message = body.error.message;
    } catch {
      // Keep the default sentence.
    }
    return { ok: false, failure: "not-configured", reason: message };
  }
  const broke: InstallResult = {
    ok: false,
    failure: "error",
    reason: "Something went wrong. Try again.",
  };
  if (!res.ok) return broke;
  try {
    const body = (await res.json()) as { destination?: Destination };
    if (!body?.destination?.id) return broke;
    return { ok: true, destination: body.destination };
  } catch {
    return broke;
  }
}

/**
 * Ask the catalogue whether one install is still good.
 *
 * Only an outright "no such code" revokes. Offline, a 500, a proxy swallowing
 * the call — all of those leave the install exactly as it was.
 */
export async function recheckInstall(
  entry: InstalledDestination,
  fetchImpl: typeof fetch = fetch,
): Promise<RecheckVerdict> {
  const result = await installByCode(entry.code, fetchImpl);
  if (result.ok) return { kind: "kept", destination: result.destination };
  return result.failure === "unknown-code"
    ? { kind: "revoked" }
    : { kind: "unknown" };
}
